import React, { useState } from 'react';
import { Hotel } from '../types';
import { Building2, Plus, Phone, MapPin, Mail, FileText, CheckCircle, XCircle, Search, Key, Shield } from 'lucide-react';
import { formatLocalDate } from '../utils/storage';

interface HotelsProps {
  hotels: Hotel[];
  selectedHotelId: string;
  onAddHotel: (hotel: Omit<Hotel, 'id' | 'createdAt'>) => void;
  onUpdateHotel: (id: string, updates: Partial<Hotel>) => void;
  onSelectHotel: (id: string) => void;
  isAdmin: boolean;
}

export default function Hotels({
  hotels,
  selectedHotelId,
  onAddHotel,
  onUpdateHotel,
  onSelectHotel,
  isAdmin,
}: HotelsProps) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Form states
  const [name, setName] = useState('');
  const [corporateName, setCorporateName] = useState('');
  const [document, setDocument] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [pixKey, setPixKey] = useState('');
  const [adminUser, setAdminUser] = useState('');
  const [adminPass, setAdminPass] = useState('');
  const [recepUser, setRecepUser] = useState('');
  const [recepPass, setRecepPass] = useState('');

  // Local inline editing states
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editCorporateName, setEditCorporateName] = useState('');
  const [editDocument, setEditDocument] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editAddress, setEditAddress] = useState('');
  const [editPixKey, setEditPixKey] = useState('');
  const [editAdminUser, setEditAdminUser] = useState('');
  const [editAdminPass, setEditAdminPass] = useState('');
  const [editRecepUser, setEditRecepUser] = useState('');
  const [editRecepPass, setEditRecepPass] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      alert('Por favor, informe pelo menos o Nome Fantasia do hotel.');
      return;
    }

    onAddHotel({
      name,
      corporateName: corporateName || name,
      document,
      phone,
      email,
      address,
      pixKey,
      adminUser: adminUser.trim() || undefined,
      adminPass: adminPass || undefined,
      recepUser: recepUser.trim() || undefined,
      recepPass: recepPass || undefined,
      active: true,
    });

    // Reset fields
    setName('');
    setCorporateName('');
    setDocument('');
    setPhone('');
    setEmail('');
    setAddress('');
    setPixKey('');
    setAdminUser('');
    setAdminPass('');
    setRecepUser('');
    setRecepPass('');
    setShowAddForm(false);
    alert('Nova unidade e acessos de segurança salvos com sucesso!');
  };

  const handleStartEdit = (h: Hotel) => {
    if (!isAdmin) return;
    setEditingId(h.id);
    setEditName(h.name);
    setEditCorporateName(h.corporateName);
    setEditDocument(h.document);
    setEditPhone(h.phone);
    setEditEmail(h.email);
    setEditAddress(h.address);
    setEditPixKey(h.pixKey);
    setEditAdminUser(h.adminUser || '');
    setEditAdminPass(h.adminPass || '');
    setEditRecepUser(h.recepUser || '');
    setEditRecepPass(h.recepPass || '');
  };

  const handleSaveEdit = (id: string) => {
    if (!editName.trim()) {
      alert('O Nome Fantasia é obrigatório.');
      return;
    }
    onUpdateHotel(id, {
      name: editName,
      corporateName: editCorporateName,
      document: editDocument,
      phone: editPhone,
      email: editEmail,
      address: editAddress,
      pixKey: editPixKey,
      adminUser: editAdminUser.trim() || undefined,
      adminPass: editAdminPass || undefined,
      recepUser: editRecepUser.trim() || undefined,
      recepPass: editRecepPass || undefined,
    });
    setEditingId(null);
    alert('Informações e códigos de acesso atualizados com sucesso!');
  };

  const filteredHotels = hotels.filter(h =>
    h.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    h.corporateName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    h.document.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-zinc-200 pb-5">
        <div>
          <h1 id="header-title-hotels" className="text-xl font-semibold tracking-tight text-zinc-950">
            Cadeia de Hotéis e Unidades
          </h1>
          <p className="text-xs text-zinc-500 mt-1">
            Cadastre novas unidades do grupo, configure os parâmetros de acesso sob demanda e altere dados cadastrais corporativos.
          </p>
        </div>
        {isAdmin && (
          <button
            type="button"
            id="btn-toggle-add-hotel"
            onClick={() => setShowAddForm(!showAddForm)}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-zinc-900 text-white font-semibold text-xs rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer"
          >
            <Plus className="w-4 h-4" />
            {showAddForm ? 'Ocultar Formulário' : 'Novo Hotel / Unidade'}
          </button>
        )}
      </div>

      {/* Add New Hotel Section */}
      {showAddForm && isAdmin && (
        <div id="section-new-hotel-form" className="bg-white border border-zinc-200 rounded-xl overflow-hidden shadow-sm animate-fade-in">
          <div className="border-b border-zinc-100 bg-zinc-50 px-4 py-3 flex items-center gap-2">
            <Building2 className="w-4 h-4 text-zinc-650" />
            <h2 className="text-xs font-bold text-zinc-800 uppercase tracking-wider">
              Cadastrar Nova Unidade / Pousada
            </h2>
          </div>
          <form onSubmit={handleSubmit} className="p-4 sm:p-5 space-y-5">
            {/* Secativa 1: Dados Gerais */}
            <div className="space-y-3">
              <span className="text-[10px] font-extrabold text-zinc-400 uppercase tracking-wider block border-b border-zinc-100 pb-1">
                A. Dados Gerais & Faturamento
              </span>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="hotel-input-name" className="block text-[10px] font-bold text-zinc-500 uppercase mb-1">
                    Nome Fantasia *
                  </label>
                  <input
                    type="text"
                    id="hotel-input-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Ex. Pousada Ventos de Geribá"
                    className="block w-full text-xs py-2 px-3 border border-zinc-200 rounded-lg text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-900 bg-zinc-50/20"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="hotel-input-corporate" className="block text-[10px] font-bold text-zinc-500 uppercase mb-1">
                    Razão Social
                  </label>
                  <input
                    type="text"
                    id="hotel-input-corporate"
                    value={corporateName}
                    onChange={(e) => setCorporateName(e.target.value)}
                    placeholder="Ex. Ventos de Geribá Hospedagens LTDA"
                    className="block w-full text-xs py-2 px-3 border border-zinc-200 rounded-lg text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-900 bg-zinc-50/20"
                  />
                </div>

                <div>
                  <label htmlFor="hotel-input-document" className="block text-[10px] font-bold text-zinc-500 uppercase mb-1">
                    CNPJ
                  </label>
                  <input
                    type="text"
                    id="hotel-input-document"
                    value={document}
                    onChange={(e) => setDocument(e.target.value)}
                    placeholder="Ex. 00.000.000/0001-00"
                    className="block w-full text-xs py-2 px-3 border border-zinc-200 rounded-lg text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-900 bg-zinc-50/20"
                  />
                </div>

                <div>
                  <label htmlFor="hotel-input-phone" className="block text-[10px] font-bold text-zinc-500 uppercase mb-1">
                    Telefone
                  </label>
                  <input
                    type="text"
                    id="hotel-input-phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="Ex. (21) 99999-8888"
                    className="block w-full text-xs py-2 px-3 border border-zinc-200 rounded-lg text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-900 bg-zinc-50/20"
                  />
                </div>

                <div>
                  <label htmlFor="hotel-input-email" className="block text-[10px] font-bold text-zinc-500 uppercase mb-1">
                    E-mail de Contato
                  </label>
                  <input
                    type="email"
                    id="hotel-input-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Ex. reservas@pousadaventos.com.br"
                    className="block w-full text-xs py-2 px-3 border border-zinc-200 rounded-lg text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-900 bg-zinc-50/20"
                  />
                </div>

                <div>
                  <label htmlFor="hotel-input-pix" className="block text-[10px] font-bold text-zinc-500 uppercase mb-1">
                    Chave PIX Recebimentos
                  </label>
                  <input
                    type="text"
                    id="hotel-input-pix"
                    value={pixKey}
                    onChange={(e) => setPixKey(e.target.value)}
                    placeholder="E-mail, CNPJ ou Celular"
                    className="block w-full text-xs py-2 px-3 border border-zinc-200 rounded-lg text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-900 bg-zinc-50/20"
                  />
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="hotel-input-address" className="block text-[10px] font-bold text-zinc-500 uppercase mb-1">
                    Endereço Físico Completo
                  </label>
                  <input
                    type="text"
                    id="hotel-input-address"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="Ex. Rua das Flores, 450 - Centro, Búzios - RJ"
                    className="block w-full text-xs py-2 px-3 border border-zinc-200 rounded-lg text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-900 bg-zinc-50/20"
                  />
                </div>
              </div>
            </div>

            {/* Seção 2: Chaves de Acesso */}
            <div className="border border-zinc-150 rounded-xl p-4 bg-zinc-50/65 space-y-3">
              <span className="text-[10px] font-extrabold text-zinc-600 uppercase tracking-wider flex items-center gap-1">
                <Shield className="w-3.5 h-3.5 text-zinc-500" />
                B. Acessos exclusivos para os Operadores desta Unidade
              </span>
              <p className="text-[10px] text-zinc-400">
                Os operadores farão login com estes dados e serão automaticamente direcionados a esta pousada. Deixe em branco para usar o padrão (admin_[slug] / recep_[slug]).
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
                {/* Admin user config */}
                <div className="bg-white p-3 rounded-lg border border-zinc-200 space-y-3">
                  <span className="font-bold text-green-700 text-[10.5px] uppercase block border-b border-zinc-100 pb-1">
                    Administrador (Unidade)
                  </span>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[9px] font-bold text-zinc-400 uppercase mb-1">Identificador</label>
                      <input
                        type="text"
                        value={adminUser}
                        onChange={(e) => setAdminUser(e.target.value)}
                        placeholder="Ex: adminventofort"
                        className="block w-full text-xs py-1.5 px-2.5 border border-zinc-200 rounded focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-[9px] font-bold text-zinc-400 uppercase mb-1">Código de Acesso</label>
                      <input
                        type="text"
                        value={adminPass}
                        onChange={(e) => setAdminPass(e.target.value)}
                        placeholder="Ex: ventoadm123"
                        className="block w-full text-xs py-1.5 px-2.5 border border-zinc-200 rounded focus:outline-none"
                      />
                    </div>
                  </div>
                </div>

                {/* Recep user config */}
                <div className="bg-white p-3 rounded-lg border border-zinc-200 space-y-3">
                  <span className="font-bold text-blue-700 text-[10.5px] uppercase block border-b border-zinc-100 pb-1">
                    Recepcionista (Unidade)
                  </span>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[9px] font-bold text-zinc-400 uppercase mb-1">Identificador</label>
                      <input
                        type="text"
                        value={recepUser}
                        onChange={(e) => setRecepUser(e.target.value)}
                        placeholder="Ex: recepventofort"
                        className="block w-full text-xs py-1.5 px-2.5 border border-zinc-200 rounded focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-[9px] font-bold text-zinc-400 uppercase mb-1">Código de Acesso</label>
                      <input
                        type="text"
                        value={recepPass}
                        onChange={(e) => setRecepPass(e.target.value)}
                        placeholder="Ex: ventorecep12"
                        className="block w-full text-xs py-1.5 px-2.5 border border-zinc-200 rounded focus:outline-none"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-zinc-100 pt-3">
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="px-3.5 py-1.5 border border-zinc-200 hover:bg-zinc-50 rounded-lg text-xxs font-bold uppercase transition-colors text-zinc-500 cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="submit"
                id="btn-submit-new-hotel"
                className="px-4 py-1.5 bg-zinc-900 hover:bg-zinc-800 text-white rounded-lg text-xxs font-bold uppercase transition-colors cursor-pointer"
              >
                Salvar Unidade
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Directory controls */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-white p-3 border border-zinc-200 rounded-lg">
        <div className="relative w-full sm:max-w-xs">
          <span className="absolute inset-y-0 left-0 flex items-center pl-2.5 text-zinc-400 pointer-events-none">
            <Search className="w-3.5 h-3.5" />
          </span>
          <input
            type="text"
            id="input-search-hotels"
            placeholder="Filtrar por nome, razão, CNPJ..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="block w-full py-1.5 pl-8 pr-3 text-xs border border-zinc-250 bg-zinc-50/40 rounded-lg focus:outline-none focus:ring-1 focus:border-zinc-900 focus:ring-zinc-950 placeholder-zinc-400"
          />
        </div>
        <div className="text-xxs font-medium text-zinc-405">
          Unidades Administradas: <strong className="text-zinc-800 font-mono font-bold text-xs">{hotels.length}</strong>
        </div>
      </div>

      {/* List (Desktop & Mobile) */}
      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden shadow-sm">
        
        {/* Desktop Table View */}
        <div className="hidden md:block overflow-x-auto">
          <table id="table-hotels" className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="bg-zinc-50 border-b border-zinc-200 text-zinc-500 font-bold uppercase tracking-wider text-xxs">
                <th className="py-2.5 px-4 w-12 text-center">#</th>
                <th className="py-2.5 px-4 min-w-[150px]">Hotel / Nome Fantasia</th>
                <th className="py-2.5 px-4 min-w-[150px]">Razão Social / CNPJ</th>
                <th className="py-2.5 px-4">Contatos</th>
                <th className="py-2.5 px-4">Localização / Chave PIX</th>
                <th className="py-2.5 px-4 min-w-[170px]">Controle de Acesso</th>
                <th className="py-2.5 px-4 text-center">Status</th>
                <th className="py-2.5 px-4 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200/85">
              {filteredHotels.length === 0 ? (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-zinc-400 text-xs">
                    Nenhum hotel localizado com as palavras-chave informadas.
                  </td>
                </tr>
              ) : (
                filteredHotels.map((h, idx) => {
                  const isEditing = editingId === h.id;
                  const isCurrentActive = selectedHotelId === h.id;

                  return (
                    <tr
                      key={h.id}
                      id={`hotel-row-${h.id}`}
                      className={`font-medium transition-colors ${
                        isCurrentActive ? 'bg-amber-50/20' : 'hover:bg-zinc-50/20'
                      }`}
                    >
                      {/* Column Index */}
                      <td className="py-4 px-4 text-center text-zinc-400 font-mono text-xxs">
                        {idx + 1}
                      </td>

                      {/* Name Card */}
                      <td className="py-4 px-4">
                        {isEditing ? (
                          <div className="space-y-1">
                            <input
                              type="text"
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              className="text-xs p-1 border border-zinc-300 rounded focus:outline-none w-full"
                            />
                            <p className="text-[10px] text-zinc-400">Nome Fantasia (Obrigatório)</p>
                          </div>
                        ) : (
                          <div>
                            <div className="font-bold text-zinc-900 text-sm flex items-center gap-1.5">
                              {h.name}
                              {isCurrentActive && (
                                <span className="text-[8px] font-bold bg-amber-500 text-zinc-950 px-1.5 py-0.5 rounded-full uppercase tracking-wider shrink-0 shadow-sm border border-amber-300">
                                  Visualizando
                                </span>
                              )}
                            </div>
                            <span className="text-[10px] text-zinc-400 block mt-0.5">Criada em: {formatLocalDate(h.createdAt || '') || '2026-05-01'}</span>
                          </div>
                        )}
                      </td>

                      {/* Razão Social / CNPJ */}
                      <td className="py-4 px-4 text-xs text-zinc-700">
                        {isEditing ? (
                          <div className="space-y-1">
                            <input
                              type="text"
                              value={editCorporateName}
                              onChange={(e) => setEditCorporateName(e.target.value)}
                              placeholder="Razão Social"
                              className="text-xs p-1 border border-zinc-300 rounded focus:outline-none w-full mb-1"
                            />
                            <input
                              type="text"
                              value={editDocument}
                              onChange={(e) => setEditDocument(e.target.value)}
                              placeholder="CNPJ"
                              className="text-xs p-1 border border-zinc-300 rounded focus:outline-none w-full font-mono"
                            />
                          </div>
                        ) : (
                          <div className="space-y-1">
                            <div className="text-zinc-800 font-semibold">{h.corporateName}</div>
                            <div className="text-[11px] font-mono text-zinc-400 col-span-1">CNPJ: {h.document || 'Não Informado'}</div>
                          </div>
                        )}
                      </td>

                      {/* Contacts */}
                      <td className="py-4 px-4 text-zinc-600 font-normal">
                        {isEditing ? (
                          <div className="space-y-1">
                            <input
                              type="text"
                              value={editPhone}
                              onChange={(e) => setEditPhone(e.target.value)}
                              placeholder="Telefone"
                              className="text-xs p-1 border border-zinc-300 rounded focus:outline-none w-full mb-1"
                            />
                            <input
                              type="email"
                              value={editEmail}
                              onChange={(e) => setEditEmail(e.target.value)}
                              placeholder="E-mail"
                              className="text-xs p-1 border border-zinc-300 rounded focus:outline-none w-full"
                            />
                          </div>
                        ) : (
                          <div className="space-y-1.5 text-[11px]">
                            <div className="flex items-center gap-1">
                              <Phone className="w-3" />
                              <span>{h.phone || '-'}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Mail className="w-3 h-3 text-zinc-400 shrink-0" />
                              <span className="truncate max-w-[160px] block">{h.email || '-'}</span>
                            </div>
                          </div>
                        )}
                      </td>

                      {/* Address / Pix key */}
                      <td className="py-4 px-4 text-zinc-650">
                        {isEditing ? (
                          <div className="space-y-1">
                            <textarea
                              value={editAddress}
                              onChange={(e) => setEditAddress(e.target.value)}
                              placeholder="Endereço"
                              rows={1}
                              className="text-xs p-1 border border-zinc-300 rounded focus:outline-none w-full mb-1"
                            />
                            <input
                              type="text"
                              value={editPixKey}
                              onChange={(e) => setEditPixKey(e.target.value)}
                              placeholder="Chave PIX"
                              className="text-xs p-1 border border-zinc-300 rounded focus:outline-none w-full"
                            />
                          </div>
                        ) : (
                          <div className="space-y-1.5 text-[11px]">
                            <div className="flex items-start gap-1 leading-tight">
                              <MapPin className="w-3 h-3 text-zinc-400 mt-0.5 shrink-0" />
                              <span className="max-w-[170px] block truncate" title={h.address}>{h.address || '-'}</span>
                            </div>
                            <div className="flex items-center gap-1 font-mono text-[10px] text-zinc-500">
                              <Key className="w-3 text-zinc-400" />
                              <span className="border-b border-dashed border-zinc-200">PIX: {h.pixKey || '-'}</span>
                            </div>
                          </div>
                        )}
                      </td>

                      {/* Credentials (Critical access info!) */}
                      <td className="py-4 px-4">
                        {isEditing ? (
                          <div className="space-y-2 p-1.5 bg-zinc-50 border border-zinc-200 rounded text-xxs">
                            <div>
                              <label className="block text-[8px] font-bold uppercase text-zinc-400">Identificador ADM</label>
                              <input
                                type="text"
                                value={editAdminUser}
                                onChange={(e) => setEditAdminUser(e.target.value)}
                                placeholder="Padrão admin..."
                                className="bg-white p-0.5 border border-zinc-300 rounded text-xxs w-full focus:outline-none"
                              />
                            </div>
                            <div>
                              <label className="block text-[8px] font-bold uppercase text-zinc-400">Código ADM</label>
                              <input
                                type="text"
                                value={editAdminPass}
                                onChange={(e) => setEditAdminPass(e.target.value)}
                                placeholder="Código padrão"
                                className="bg-white p-0.5 border border-zinc-300 rounded text-xxs w-full focus:outline-none"
                              />
                            </div>
                            <div className="border-t border-zinc-200 my-1 pt-1">
                              <label className="block text-[8px] font-bold uppercase text-zinc-400">Identificador Recepcionista</label>
                              <input
                                type="text"
                                value={editRecepUser}
                                onChange={(e) => setEditRecepUser(e.target.value)}
                                placeholder="Padrão recep..."
                                className="bg-white p-0.5 border border-zinc-300 rounded text-xxs w-full focus:outline-none"
                              />
                            </div>
                            <div>
                              <label className="block text-[8px] font-bold uppercase text-zinc-400">Código Recepcionista</label>
                              <input
                                type="text"
                                value={editRecepPass}
                                onChange={(e) => setEditRecepPass(e.target.value)}
                                placeholder="Código padrão"
                                className="bg-white p-0.5 border border-zinc-300 rounded text-xxs w-full focus:outline-none"
                              />
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-1 text-xxs font-mono bg-zinc-50 p-2 border border-zinc-150 rounded leading-relaxed text-zinc-700">
                            <div>
                              <span className="font-extrabold text-green-700 uppercase select-none text-[8px] bg-green-50 px-1 rounded mr-1">ADM</span>
                              <strong className="text-zinc-900">{h.adminUser || `admin${idx+1} [padrão]`}</strong> 
                              <span className="text-zinc-400 ml-1">({h.adminPass || 'admin'})</span>
                            </div>
                            <div className="border-t border-zinc-100 mt-1 pt-1">
                              <span className="font-extrabold text-blue-700 uppercase select-none text-[8px] bg-blue-50 px-1 rounded mr-1">RECEP</span>
                              <strong className="text-zinc-900">{h.recepUser || `recep${idx+1} [padrão]`}</strong>
                              <span className="text-zinc-400 ml-1">({h.recepPass || 'recep'})</span>
                            </div>
                          </div>
                        )}
                      </td>

                      {/* Active Status */}
                      <td className="py-4 px-4 text-center">
                        {h.active ? (
                          <span className="inline-flex px-2 py-0.5 rounded text-[10px] font-bold bg-green-50 text-green-700 uppercase border border-green-200">
                            Ativo
                          </span>
                        ) : (
                          <span className="inline-flex px-2 py-0.5 rounded text-[10px] font-bold bg-zinc-100 text-zinc-400 uppercase border border-zinc-200">
                            Inativo
                          </span>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="py-4 px-4 text-right">
                        <div className="flex items-center justify-end gap-1.5 text-xxs">
                          {isEditing ? (
                            <div className="flex flex-col gap-1">
                              <button
                                type="button"
                                onClick={() => handleSaveEdit(h.id)}
                                className="px-2 py-1 bg-green-600 hover:bg-green-700 text-white font-bold uppercase rounded cursor-pointer"
                              >
                                Gravar
                              </button>
                              <button
                                type="button"
                                onClick={() => setEditingId(null)}
                                className="px-2 py-1 border border-zinc-300 text-zinc-500 hover:bg-zinc-50 font-bold uppercase rounded cursor-pointer"
                              >
                                Sair
                              </button>
                            </div>
                          ) : (
                            <>
                              {/* Toggle active status (Admin only) */}
                              {isAdmin && (
                                <button
                                  type="button"
                                  id={`btn-toggle-hotel-status-${h.id}`}
                                  onClick={() => {
                                    const confirmText = h.active
                                      ? `Tem certeza que deseja DESATIVAR a unidade "${h.name}"?\nIsso impedirá novas conexões de operadores.`
                                      : `Deseja ATIVAR a unidade "${h.name}"?`;
                                    if (confirm(confirmText)) {
                                      onUpdateHotel(h.id, { active: !h.active });
                                    }
                                  }}
                                  className={`px-2 py-1 font-bold uppercase rounded border transition-colors cursor-pointer ${
                                    h.active
                                      ? 'border-red-200 text-red-600 hover:bg-red-50'
                                      : 'border-green-200 text-green-700 hover:bg-green-50'
                                  }`}
                                >
                                  {h.active ? 'Inativar' : 'Ativar'}
                                </button>
                              )}

                              {/* Edit details */}
                              {isAdmin && (
                                <button
                                  type="button"
                                  id={`btn-edit-hotel-${h.id}`}
                                  onClick={() => handleStartEdit(h)}
                                  className="px-2 py-1 border border-zinc-200 hover:bg-zinc-50 font-bold uppercase rounded text-zinc-650 cursor-pointer"
                                >
                                  Editar
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Card List View */}
        <div className="md:hidden divide-y divide-zinc-200 bg-white">
          {filteredHotels.length === 0 ? (
            <div className="p-8 text-center text-zinc-400 font-normal text-xs">
              Nenhum hotel cadastrado ou localizado.
            </div>
          ) : (
            filteredHotels.map((h, idx) => {
              const isEditing = editingId === h.id;
              const isCurrentActive = selectedHotelId === h.id;

              return (
                <div key={h.id} id={`hotel-card-mobile-${h.id}`} className="p-4 space-y-4 text-xs">
                  {/* Basic Info */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 space-y-1">
                      {isEditing ? (
                        <div className="space-y-1.5">
                          <label className="block text-[9px] font-bold uppercase text-zinc-400 font-sans">Nome Fantasia *</label>
                          <input
                            type="text"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            className="text-xs p-1.5 border border-zinc-300 rounded focus:outline-none w-full"
                          />
                        </div>
                      ) : (
                        <div className="font-semibold text-zinc-900 text-sm leading-tight flex flex-wrap items-center gap-1.5">
                          {h.name}
                          {isCurrentActive && (
                            <span className="text-[8px] font-bold bg-amber-500 text-zinc-950 px-1.5 py-0.2 rounded uppercase tracking-wider">
                              Ativo
                            </span>
                          )}
                        </div>
                      )}
                      <p className="text-[10px] text-zinc-400"># {idx + 1} | Criada em {formatLocalDate(h.createdAt || '') || '2026-05-01'}</p>
                    </div>
                  </div>

                  {/* Corporate and CNPJ info */}
                  <div className="bg-zinc-50 border border-zinc-150 p-3 rounded-lg space-y-2.5 text-xs text-zinc-700">
                    <div>
                      <span className="block font-bold text-zinc-400 uppercase text-[9px] tracking-wide mb-1">Razão Social & CNPJ</span>
                      {isEditing ? (
                        <div className="space-y-1.5 mt-1">
                          <input
                            type="text"
                            value={editCorporateName}
                            onChange={(e) => setEditCorporateName(e.target.value)}
                            placeholder="Razão Social"
                            className="bg-white text-xs p-1.5 border border-zinc-300 rounded focus:outline-none w-full"
                          />
                          <input
                            type="text"
                            value={editDocument}
                            onChange={(e) => setEditDocument(e.target.value)}
                            placeholder="CNPJ"
                            className="bg-white text-xs p-1.5 border border-zinc-300 rounded focus:outline-none w-full font-mono"
                          />
                        </div>
                      ) : (
                        <div className="font-semibold text-zinc-800 leading-tight">
                          {h.corporateName} <br />
                          <span className="text-[10px] text-zinc-500 font-mono">CNPJ: {h.document || 'N/D'}</span>
                        </div>
                      )}
                    </div>

                    {/* Contact Details in Mobile */}
                    <div className="border-t border-zinc-200/60 pt-2 space-y-1.5">
                      <span className="block font-bold text-zinc-400 uppercase text-[9px] tracking-wide mb-1">Contato & Localização</span>
                      {isEditing ? (
                        <div className="space-y-1.5 mt-1">
                          <input
                            type="text"
                            value={editPhone}
                            onChange={(e) => setEditPhone(e.target.value)}
                            placeholder="Telefone"
                            className="bg-white text-xs p-1.5 border border-zinc-300 rounded-focus:outline-none w-full"
                          />
                          <input
                            type="email"
                            value={editEmail}
                            onChange={(e) => setEditEmail(e.target.value)}
                            placeholder="E-mail Administrativo"
                            className="bg-white text-xs p-1.5 border border-zinc-300 rounded focus:outline-none w-full"
                          />
                          <input
                            type="text"
                            value={editAddress}
                            onChange={(e) => setEditAddress(e.target.value)}
                            placeholder="Endereço Físico"
                            className="bg-white text-xs p-1.5 border border-zinc-300 rounded focus:outline-none w-full"
                          />
                          <input
                            type="text"
                            value={editPixKey}
                            onChange={(e) => setEditPixKey(e.target.value)}
                            placeholder="Chave Recebimentos PIX"
                            className="bg-white text-xs p-1.5 border border-zinc-300 rounded focus:outline-none w-full"
                          />
                        </div>
                      ) : (
                        <div className="space-y-1 text-zinc-650 text-[11px]">
                          <div className="flex items-center gap-1.5">
                            <Phone className="w-3.5 h-3.5 text-zinc-400" />
                            <span>{h.phone || 'N/D'}</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <Mail className="w-3.5 h-3.5 text-zinc-400" />
                            <span>{h.email || 'N/D'}</span>
                          </div>
                          <div className="flex items-start gap-1.5">
                            <MapPin className="w-3.5 h-3.5 text-zinc-400 shrink-0 mt-0.5" />
                            <span>{h.address || 'N/D'}</span>
                          </div>
                          <div className="flex items-center gap-1.5 font-mono text-[10px] text-zinc-500 pt-1 border-t border-dashed border-zinc-200">
                            <Key className="w-3.5 h-3.5 text-zinc-400" />
                            <span>PIX: {h.pixKey || 'N/D'}</span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Mobile Credentials Block */}
                    <div className="border-t border-zinc-200/60 pt-2 space-y-1">
                      <span className="block font-bold text-zinc-400 uppercase text-[9px] tracking-wide mb-1">Controle de Acesso exclusivo</span>
                      {isEditing ? (
                        <div className="space-y-2 p-2 bg-white rounded border border-zinc-200 text-xxs">
                          <div>
                            <label className="block text-[8px] font-bold uppercase text-zinc-400">Identificador ADM</label>
                            <input
                              type="text"
                              value={editAdminUser}
                              onChange={(e) => setEditAdminUser(e.target.value)}
                              className="bg-white p-1 border border-zinc-300 rounded w-full text-xs"
                            />
                          </div>
                          <div>
                            <label className="block text-[8px] font-bold uppercase text-zinc-400">Código ADM</label>
                            <input
                              type="text"
                              value={editAdminPass}
                              onChange={(e) => setEditAdminPass(e.target.value)}
                              className="bg-white p-1 border border-zinc-300 rounded w-full text-xs"
                            />
                          </div>
                          <div className="border-t border-zinc-200 pt-1 mt-1">
                            <label className="block text-[8px] font-bold uppercase text-zinc-400">Identificador RECEP</label>
                            <input
                              type="text"
                              value={editRecepUser}
                              onChange={(e) => setEditRecepUser(e.target.value)}
                              className="bg-white p-1 border border-zinc-350 rounded w-full text-xs"
                            />
                          </div>
                          <div>
                            <label className="block text-[8px] font-bold uppercase text-zinc-400">Código RECEP</label>
                            <input
                              type="text"
                              value={editRecepPass}
                              onChange={(e) => setEditRecepPass(e.target.value)}
                              className="bg-white p-1 border border-zinc-300 rounded w-full text-xs"
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-1.5 font-mono text-xxs bg-white/70 p-2 border border-zinc-150 rounded leading-relaxed text-zinc-700">
                          <div>
                            <span className="font-extrabold text-green-700 uppercase text-[8px] bg-green-50 px-1 rounded mr-1">ADM</span>
                            <strong>{h.adminUser || `admin${idx+1} [padrão]`}</strong> / <span className="text-zinc-650">{h.adminPass || 'admin'}</span>
                          </div>
                          <div>
                            <span className="font-extrabold text-blue-700 uppercase text-[8px] bg-blue-50 px-1 rounded mr-1">RECEP</span>
                            <strong>{h.recepUser || `recep${idx+1} [padrão]`}</strong> / <span className="text-zinc-650">{h.recepPass || 'recep'}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions & Status */}
                  <div className="flex items-center justify-between border-t border-zinc-100 pt-3">
                    <div>
                      {h.active ? (
                        <span className="inline-flex px-2 py-0.5 rounded text-[10px] font-bold bg-green-50 text-green-700 uppercase border border-green-200">Ativa</span>
                      ) : (
                        <span className="inline-flex px-2 py-0.5 rounded text-[10px] font-bold bg-zinc-100 text-zinc-400 uppercase border border-zinc-200">Inativo</span>
                      )}
                    </div>

                    <div className="flex items-center gap-1.5">
                      {isEditing ? (
                        <>
                          <button
                            type="button"
                            onClick={() => handleSaveEdit(h.id)}
                            className="px-3.5 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xxs font-bold uppercase cursor-pointer"
                          >
                            Salvar
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditingId(null)}
                            className="px-2.5 py-1.5 border border-zinc-200 rounded-lg text-xxs font-bold uppercase hover:bg-zinc-55 cursor-pointer"
                          >
                            Voltar
                          </button>
                        </>
                      ) : (
                        <>
                          {isAdmin && (
                            <button
                              type="button"
                              onClick={() => {
                                const confirmText = h.active
                                  ? `Tem certeza que deseja DESATIVAR a unidade "${h.name}"?`
                                  : `Deseja ATIVAR a unidade "${h.name}"?`;
                                if (confirm(confirmText)) {
                                  onUpdateHotel(h.id, { active: !h.active });
                                }
                              }}
                              className={`px-2.5 py-1.5 rounded-lg text-[10px] font-bold uppercase cursor-pointer border ${
                                h.active
                                  ? 'border-red-200 text-red-600 hover:bg-red-50'
                                  : 'border-green-200 text-green-700 hover:bg-green-50'
                              }`}
                            >
                              {h.active ? 'Inativar' : 'Ativar'}
                            </button>
                          )}

                          {isAdmin && (
                            <button
                              type="button"
                              onClick={() => handleStartEdit(h)}
                              className="px-2.5 py-1.5 border border-zinc-200 hover:bg-zinc-50 text-[10px] font-bold uppercase rounded-lg text-zinc-500 cursor-pointer"
                            >
                              Editar
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

      </div>
    </div>
  );
}
